// src/pages/NewUserPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

function NewUserPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    lastname: '',
    cedula: '',
    profession: '',
    role: 'medico', // CAMBIO: Ahora el valor por defecto es 'medico' según tu lógica
    license: '',
    email: '',
    password: '',
    username: '', 
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [usernameExists, setUsernameExists] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  useEffect(() => {
    if (formData.lastname && formData.cedula) {
      const newUsername = `${formData.lastname.slice(0, 3).toLowerCase()}-${formData.cedula}`;
      setFormData(prev => ({ ...prev, username: newUsername }));
    } else {
      setFormData(prev => ({ ...prev, username: '' }));
    }
  }, [formData.lastname, formData.cedula]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (usernameExists) {
      setError('El nombre de usuario ya existe. Por favor, revise los datos.');
      return;
    }

    try {
      const response = await fetch('https://luiatro-health-cloud-production.up.railway.app/api/users', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear el usuario');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/users');
      }, 1500);

    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/users')}
        sx={{ mb: 2 }}
      >
        Volver a la lista
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Añadir Nuevo Usuario
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>¡Usuario creado con éxito!</Alert>}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nombre" name="name" value={formData.name} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Apellido" name="lastname" value={formData.lastname} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Cédula" name="cedula" value={formData.cedula} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Profesión" name="profession" value={formData.profession} onChange={handleChange} />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="role-label">Rol del Usuario</InputLabel>
                <Select 
                  labelId="role-label" 
                  name="role" 
                  value={formData.role} 
                  label="Rol del Usuario" 
                  onChange={handleChange}
                >
                  {/* AJUSTE DE ROLES SINCRONIZADOS CON EL SISTEMA */}
                  <MenuItem value="administrador">Administrador</MenuItem>
                  <MenuItem value="medico">Médico Tratante</MenuItem>
                  <MenuItem value="asistente">Asistente Administrativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Matrícula Profesional" name="license" value={formData.license} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="email" label="Correo Electrónico" name="email" value={formData.email} onChange={handleChange} required />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                type={showPassword ? 'text' : 'password'} 
                label="Contraseña" 
                name="password" 
                value={formData.password} 
                onChange={handleChange} 
                required 
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de Usuario (Generado)"
                name="username"
                value={formData.username}
                InputProps={{ 
                  readOnly: true,
                  sx: { backgroundColor: '#f9f9f9', fontWeight: 'bold' } 
                }}
                helperText="Fórmula: 3 letras del apellido + cédula"
              />
              {usernameExists && <Alert severity="warning" sx={{ mt: 1 }}>¡Este nombre de usuario ya existe!</Alert>}
            </Grid>
            
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2, px: 5 }} size="large">
                Crear Usuario
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default NewUserPage;